import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { useCustomDragDrop, DragItem } from './useCustomDragDrop'

const DragDropContext = createContext<{
  dragState: ReturnType<typeof useCustomDragDrop>['dragState']
  startDrag: ReturnType<typeof useCustomDragDrop>['startDrag']
  endDrag: (e: MouseEvent | TouchEvent) => void
  onDragEnd: (source: DragItem, destination: { droppableId: string; index: number } | null) => void
} | null>(null)

export function useDragDropContext() {
  const context = useContext(DragDropContext)
  if (!context) {
    throw new Error('useDragDropContext must be used within DragDropProvider')
  }
  return context
}

export function DragDropProvider({
  children,
  onDragEnd,
}: {
  children: React.ReactNode
  onDragEnd: (source: DragItem, destination: { droppableId: string; index: number } | null) => void
}) {
  const { dragState, startDrag, endDrag } = useCustomDragDrop()

  // Set up global listeners for drag end
  useEffect(() => {
    if (!dragState.isDragging) return

    const handleMouseUp = (e: MouseEvent) => {
      if (dragState.isDragging) {
        e.preventDefault()
        e.stopPropagation()
        endDrag(e, onDragEnd)
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (dragState.isDragging) {
        e.preventDefault()
        e.stopPropagation()
        endDrag(e, onDragEnd)
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [dragState.isDragging, endDrag, onDragEnd])

  const handleEndDragWrapper = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (dragState.isDragging) {
        endDrag(e, onDragEnd)
      }
    },
    [dragState.isDragging, endDrag, onDragEnd]
  )

  return (
    <DragDropContext.Provider value={{ dragState, startDrag, endDrag: handleEndDragWrapper, onDragEnd }}>
      {children}
    </DragDropContext.Provider>
  )
}
